import { queryOptions, useQuery } from '@tanstack/react-query';
import {
  fetchSkuDetail,
  fetchStyleDetail,
  fetchVariantDetail,
} from '@/api/my-styles';
import type {
  DetailEntityKind,
  SkuDetail,
  StyleDetail,
  VariantDetail,
} from '@/api/my-styles/types';

/** URL-segment form of the kind (`/my-styles/{kind}/{id}/...`). */
export type EntityKindPath = 'styles' | 'variants' | 'skus';

const PATH_TO_KIND: Record<EntityKindPath, DetailEntityKind> = {
  styles: 'style',
  variants: 'variant',
  skus: 'sku',
};

const KIND_TO_PATH: Record<DetailEntityKind, EntityKindPath> = {
  style: 'styles',
  variant: 'variants',
  sku: 'skus',
};

export function pathToKind(path: EntityKindPath): DetailEntityKind {
  return PATH_TO_KIND[path];
}

export function kindToPath(kind: DetailEntityKind): EntityKindPath {
  return KIND_TO_PATH[kind];
}

export function isEntityKindPath(value: string): value is EntityKindPath {
  return value === 'styles' || value === 'variants' || value === 'skus';
}

export type EntityDetail = StyleDetail | VariantDetail | SkuDetail;

export function styleDetailQueryOptions(id: string) {
  return queryOptions({
    queryKey: ['my-styles', 'detail', 'style', id] as const,
    queryFn: () => fetchStyleDetail(id),
    enabled: Boolean(id),
  });
}

export function variantDetailQueryOptions(id: string) {
  return queryOptions({
    queryKey: ['my-styles', 'detail', 'variant', id] as const,
    queryFn: () => fetchVariantDetail(id),
    enabled: Boolean(id),
  });
}

export function skuDetailQueryOptions(id: string) {
  return queryOptions({
    queryKey: ['my-styles', 'detail', 'sku', id] as const,
    queryFn: () => fetchSkuDetail(id),
    enabled: Boolean(id),
  });
}

export function entityDetailQueryOptions(kind: EntityKindPath, id: string) {
  if (kind === 'styles') return styleDetailQueryOptions(id);
  if (kind === 'variants') return variantDetailQueryOptions(id);
  return skuDetailQueryOptions(id);
}

export function useEntityDetail(kind: EntityKindPath, id: string) {
  return useQuery({
    queryKey: ['my-styles', 'detail', PATH_TO_KIND[kind], id] as const,
    queryFn: () => {
      if (kind === 'styles')
        return fetchStyleDetail(id) as Promise<EntityDetail>;
      if (kind === 'variants')
        return fetchVariantDetail(id) as Promise<EntityDetail>;
      return fetchSkuDetail(id) as Promise<EntityDetail>;
    },
    enabled: Boolean(id),
  });
}
